import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseDropdown from '@restart/ui/Dropdown';
import useEventCallback from '@restart/hooks/useEventCallback';

import MetaDropdownContext from '@components/MetaDropdown/Group/MetaDropdownContext';
import MetaDropdownItem from '@components/MetaDropdown/Group/MetaDropdownItem';
import MetaDropdownMenu, { getMetaDropdownMenuPlacement } from '@components/MetaDropdown/Group/MetaDropdownMenu';
import MetaDropdownToggle from '@components/MetaDropdown/Group/MetaDropdownToggle';
import createMetaComponent from '@components/MetaDropdown/Group/createMetaComponent';
import { classNames } from '@utils/classNames';


const MetaDropdownHeader = createMetaComponent({
  displayName: 'meta-dropdown-header',
  defaultProps: { role: 'heading' },
});
const MetaDropdownDivider = createMetaComponent({
  displayName: 'meta-dropdown-divider',
  Component: 'hr',
  defaultProps: { role: 'separator' },
});
const MetaDropdownItemText = createMetaComponent({
  displayName: 'meta-dropdown-item-text',
  Component: 'span',
});


const propTypes = {
  drop: PropTypes.oneOf(['up', 'start', 'end', 'down']),
  as: PropTypes.elementType,
  align: PropTypes.string,
  show: PropTypes.bool,
  flip: PropTypes.bool,
  onToggle: PropTypes.func,
  onSelect: PropTypes.func,
  focusFirstItemOnShow: PropTypes.oneOf([false, true, 'keyboard']),
  navbar: PropTypes.bool,
  autoClose: PropTypes.oneOf([true, 'outside', 'inside', false]),
  isRTL: PropTypes.bool,
  theme: PropTypes.oneOf(['blue-bg', 'white-bg', 'gray-bg']),
  inverted: PropTypes.bool,
};

const defaultProps = {
  navbar: false,
  align: 'start',
  autoClose: true,
  isRTL: false,
  theme: 'white-bg',
  inverted: false,
};

const MetaDropdown =
  React.forwardRef((pProps, ref) => {
    const {
      drop,
      show,
      className,
      align,
      onSelect,
      onToggle,
      focusFirstItemOnShow,
      as: Component = 'div',
      navbar: _4,
      autoClose,
      isRTL,
      theme,
      inverted,
      ...props
    } = pProps;

    const prefix = 'dropdown';

    const isClosingPermitted = (source) => {
      // autoClose=false only permits close on button click
      if (autoClose === false) return source === 'click';

      // autoClose=inside doesn't permit close on rootClose
      if (autoClose === 'inside') return source !== 'rootClose';

      // autoClose=outside doesn't permit close on select
      if (autoClose === 'outside') return source !== 'select';

      return true;
    };

    const handleToggle = useEventCallback(
      (nextShow, meta) => {
        if (
          meta.originalEvent && meta.originalEvent.currentTarget === document &&
          (meta.source !== 'keydown' ||
            (meta.originalEvent).key === 'Escape')
        )
          meta.source = 'rootClose';

        if (isClosingPermitted(meta.source)) onToggle?.(nextShow, meta);
      },
    );

    const alignEnd = align === 'end';
    const placement = getMetaDropdownMenuPlacement(alignEnd, drop, isRTL);

    const contextValue = useMemo(
      () => ({
        align,
        drop,
        isRTL,
        theme,
        inverted
      }),
      [align, drop, isRTL, theme, inverted],
    );

    return (
      <MetaDropdownContext.Provider value={contextValue}>
        <BaseDropdown
          placement={placement}
          show={show}
          onSelect={onSelect}
          onToggle={handleToggle}
          focusFirstItemOnShow={focusFirstItemOnShow}
          itemSelector={`.${prefix}-item:not(.disabled):not(:disabled)`}
        >
          <Component
            {...props}
            ref={ref}
            className={classNames(
              'relative',
              className,
              show && 'show',
              (!drop || drop === 'down') && prefix,
              drop === 'up' && 'dropup',
              drop === 'end' && 'dropend',
              drop === 'start' && 'dropstart',
            )}
          />
        </BaseDropdown>
      </MetaDropdownContext.Provider>
    );
  });

MetaDropdown.displayName = 'MetaDropdown';
MetaDropdown.propTypes = propTypes;
MetaDropdown.defaultProps = defaultProps;

export default Object.assign(MetaDropdown, {
  Toggle: MetaDropdownToggle,
  Menu: MetaDropdownMenu,
  Item: MetaDropdownItem,
  ItemText: MetaDropdownItemText,
  Divider: MetaDropdownDivider,
  Header: MetaDropdownHeader,
});